import type { Vec2, Ring, PolygonWithHoles, AABB } from '../geo/types';
import type { Layout } from '../data/types';
import type { GroundKind, GroundPiece } from './types';
import {
  union,
  unionTwo,
  difference,
  intersection,
  bufferPolyline,
  fromRing,
  simplify,
} from '../geo/clip';
import { minAreaOBB, ringBounds } from '../geo/polygon';
import { pieceKeys } from './pieceKeys';

const SIMPLIFY_M = 0.1;
const GARDEN_DITCH_SPACING_M = 1.5;
const GARDEN_LIKE: readonly GroundKind[] = ['garden', 'orchard', 'cloister'];

interface Region {
  readonly id: string;
  readonly kind: GroundKind;
  readonly polygons: readonly PolygonWithHoles[];
  readonly bounds: AABB;
  readonly ditchAngle?: number;
  readonly ditchSpacing?: number;
}

interface Tier {
  readonly regions: readonly Region[];
  readonly union: readonly PolygonWithHoles[];
}

interface Share {
  readonly region: Region;
  readonly polygon: PolygonWithHoles;
}

export interface BufferedStreets {
  readonly streetPolygons: readonly PolygonWithHoles[];
  readonly dikePolygons: readonly PolygonWithHoles[];
}

function boundsOf(polygons: readonly PolygonWithHoles[]): AABB {
  const boxes = polygons.map((polygon) => ringBounds(polygon.outer));
  return {
    minX: Math.min(...boxes.map((b) => b.minX)),
    minY: Math.min(...boxes.map((b) => b.minY)),
    maxX: Math.max(...boxes.map((b) => b.maxX)),
    maxY: Math.max(...boxes.map((b) => b.maxY)),
  };
}

function boxesOverlap(a: AABB, b: AABB): boolean {
  return a.minX <= b.maxX && b.minX <= a.maxX && a.minY <= b.maxY && b.minY <= a.maxY;
}

function region(
  id: string,
  kind: GroundKind,
  polygons: readonly PolygonWithHoles[],
  ditch: { readonly ditchAngle?: number; readonly ditchSpacing?: number } = {}
): Region[] {
  const simplified = simplify(polygons, SIMPLIFY_M).filter((p) => p.outer.length >= 3);
  return simplified.length > 0
    ? [{ id, kind, polygons: simplified, bounds: boundsOf(simplified), ...ditch }]
    : [];
}

function ringRegion(id: string, kind: GroundKind, ring: readonly Vec2[] | undefined): Region[] {
  return ring && ring.length >= 3 ? region(id, kind, [fromRing(ring)]) : [];
}

function onSide(v: number, low: number, high: number): boolean {
  return v === low || v === high;
}

function onBounds(p: Vec2, b: AABB): boolean {
  return onSide(p[0], b.minX, b.maxX) || onSide(p[1], b.minY, b.maxY);
}

function sameSide(p: Vec2, q: Vec2, b: AABB): boolean {
  return (
    (onSide(p[0], b.minX, b.maxX) && p[0] === q[0]) ||
    (onSide(p[1], b.minY, b.maxY) && p[1] === q[1])
  );
}

// Where the water's outline meets the bounds.
function exitsOf(water: readonly PolygonWithHoles[], b: AABB): readonly Vec2[] {
  return water
    .flatMap((p) => [p.outer, ...p.holes])
    .flatMap((ring) => ring.filter((p) => onBounds(p, b)));
}

function distance(p: Vec2, q: Vec2): number {
  return Math.hypot(q[0] - p[0], q[1] - p[1]);
}

function nearestExit(p: Vec2, exits: readonly Vec2[], b: AABB): Vec2 | null {
  const beside = exits.filter((e) => sameSide(p, e, b) && distance(p, e) <= SIMPLIFY_M);
  return beside.reduce<Vec2 | null>(
    (best, e) => (best && distance(p, best) <= distance(p, e) ? best : e),
    null
  );
}

// A region's corner on the bounds within the simplification of a point where the water leaves them
// is put on that point. Left beside it, the sliver of other ground between the two gave the far
// ring a stop a few centimetres from the bank it carries on out, which a shallow bank passes
// within millimetres (the Gaasp's south bank at the east bound).
function toExits(r: Region, exits: readonly Vec2[], b: AABB): Region {
  if (!r.polygons.some((polygon) => polygon.outer.some((p) => onBounds(p, b)))) return r;
  const isCorner = (p: Vec2) => onSide(p[0], b.minX, b.maxX) && onSide(p[1], b.minY, b.maxY);
  const snap = (p: Vec2): Vec2 => (isCorner(p) ? p : (nearestExit(p, exits, b) ?? p));
  const polygons = r.polygons.map((polygon) => ({
    outer: polygon.outer.map(snap),
    holes: polygon.holes.map((hole) => hole.map(snap)),
  }));
  return { ...r, polygons, bounds: boundsOf(polygons) };
}

// Cut to the bounds, where the far ring takes over.
function toTier(regions: readonly Region[], inside: readonly PolygonWithHoles[]): Tier {
  return { regions, union: intersection(union(regions.flatMap((r) => r.polygons)), inside) };
}

// PLAN §5 steps 3 and 4: streets and dikes are one region per kind group; every other region is
// its own layout feature, in the order squares, gardens, city, lastage, polders, north shore, meadow.
function buildTiers(
  layout: Layout,
  bounds: AABB,
  streets: BufferedStreets,
  exits: readonly Vec2[]
): readonly Tier[] {
  const meadow: Ring = [
    [bounds.minX, bounds.minY],
    [bounds.maxX, bounds.minY],
    [bounds.maxX, bounds.maxY],
    [bounds.minX, bounds.maxY],
  ];
  return [
    region('street', 'street', streets.streetPolygons),
    region('dike', 'dike', streets.dikePolygons),
    layout.squares.flatMap((sq) => ringRegion(`square_${sq.name}`, 'square', sq.polygon)),
    layout.gardens.flatMap((garden) => ringRegion(garden.id, garden.kind, garden.polygon)),
    [
      ...ringRegion('cityEnclosure', 'city', layout.land.cityEnclosure),
      ...ringRegion('lastage', 'lastage', layout.land.lastage),
    ],
    layout.polders.flatMap((polder) =>
      region(polder.id, 'polder', [fromRing(polder.polygon as Ring)], {
        ditchAngle: polder.ditchAngle,
        ditchSpacing: polder.ditchSpacing,
      })
    ),
    ringRegion('northShore', 'northShore', layout.land.northShore),
    region('meadow', 'meadow', [fromRing(meadow)]),
  ]
    .filter((regions) => regions.length > 0)
    .map((regions) =>
      toTier(
        regions.map((r) => toExits(r, exits, bounds)),
        [fromRing(meadow)]
      )
    );
}

// An unclaimed piece is shared among the regions of its tier in their order, so every share
// carries the id, kind and ditches of the one region it lies in.
function shareOut(piece: PolygonWithHoles, regions: readonly Region[]): readonly Share[] {
  const box = ringBounds(piece.outer);
  const candidates = regions.filter((r) => boxesOverlap(box, r.bounds));
  if (candidates.length === 1) return [{ region: candidates[0]!, polygon: piece }];

  const shares: Share[] = [];
  let remaining: readonly PolygonWithHoles[] = [piece];
  for (const candidate of candidates) {
    if (remaining.length === 0) break;
    const inside = intersection(remaining, candidate.polygons);
    shares.push(...inside.map((polygon) => ({ region: candidate, polygon })));
    remaining = difference(remaining, candidate.polygons);
  }
  return shares;
}

function toPiece({ region: r, polygon }: Share, index: number, key: string): GroundPiece {
  const gardenLike = (r.ditchAngle ?? 0) === 0 && GARDEN_LIKE.includes(r.kind);
  const axis = gardenLike ? minAreaOBB(polygon.outer).axisU : null;
  return {
    id: `${r.id}_${index}`,
    index,
    regionId: r.id,
    key,
    kind: r.kind,
    polygon,
    ditchAngle: axis ? Math.atan2(axis[1], axis[0]) : (r.ditchAngle ?? 0),
    ditchSpacing: axis ? GARDEN_DITCH_SPACING_M : (r.ditchSpacing ?? 0),
  };
}

// PLAN §5 step 4: claimed starts as the water, and each region takes what is left of it. Only the
// regions going in are simplified: every cut is kept as it comes, so the pieces share their
// outlines with the water and with each other, and no sliver between them is left out.
export function buildGroundPartition(
  layout: Layout,
  water: readonly PolygonWithHoles[],
  bounds: AABB,
  bufferedStreets?: BufferedStreets
): readonly GroundPiece[] {
  const tiers = buildTiers(
    layout,
    bounds,
    bufferedStreets ?? bufferAllStreets(layout),
    exitsOf(water, bounds)
  );
  const shares: Share[] = [];
  let claimed = water;

  for (const tier of tiers) {
    for (const piece of difference(tier.union, claimed)) {
      shares.push(...shareOut(piece, tier.regions));
    }
    claimed = unionTwo(claimed, tier.union);
  }

  const keys = pieceKeys(
    shares.map((share) => ({ regionId: share.region.id, polygon: share.polygon }))
  );
  return shares.map((share, index) => toPiece(share, index, keys[index]!));
}

function streetGround(street: Layout['streets'][number]): readonly PolygonWithHoles[] {
  return bufferPolyline(street.centerline, street.width / 2, 'round');
}

export function bufferAllStreets(layout: Layout): BufferedStreets {
  const isDike = (kind: string) => ['dike', 'road', 'path'].includes(kind);
  const buffer = (dike: boolean) =>
    layout.streets.filter((street) => isDike(street.kind) === dike).flatMap(streetGround);
  return { streetPolygons: buffer(false), dikePolygons: buffer(true) };
}
